// Tek kaynaklı engine adresi. Vite env ile ezilebilir (docs/api-reference.md: SEMANTICAD_ENGINE_URL).
// CLI aynı önceliği SEMANTICAD_ENGINE_URL env değişkeniyle uygular.
import { getBearerToken, handleUnauthorizedSession } from "./authSession";

/**
 * Default engine base:
 * - Vite dev: same-origin "" so requests hit the dev proxy (vite.config.ts → :9472).
 *   Avoids CORS and localhost vs 127.0.0.1 origin mismatch.
 * - Production / explicit env: absolute engine URL.
 */
const DEFAULT_ENGINE_BASE =
  typeof import.meta !== "undefined" && import.meta.env?.DEV ? "" : "http://127.0.0.1:9472";

export const ENGINE_BASE: string = (() => {
  const raw = import.meta.env?.VITE_SEMANTICAD_ENGINE_URL as string | undefined;
  if (raw === undefined || raw === null) return DEFAULT_ENGINE_BASE;
  const trimmed = String(raw).trim().replace(/\/+$/, "");
  // Empty string in .env means "use Vite proxy / same origin".
  return trimmed;
})();

/**
 * Engine API key for Stage 0 auth (web fleet shares one fleet; keys map to tenants).
 * Set VITE_SEMANTICAD_API_KEY to the secret half of SEMANTICAD_API_KEYS (e.g. org_a:…).
 * 
 */
export const ENGINE_API_KEY: string =
  (import.meta.env?.VITE_SEMANTICAD_API_KEY as string | undefined)?.trim() ||
  (import.meta.env?.VITE_ENGINE_API_KEY as string | undefined)?.trim() ||
  "";

function applyAuthHeaders(headers: Record<string, string>): void {
  // Prefer web session Bearer (localStorage); HttpOnly cookie travels via credentials:include.
  // Fall back to build-time API key (demo).
  const bearer = getBearerToken();
  if (bearer) {
    headers["Authorization"] = `Bearer ${bearer}`;
    return;
  }
  if (ENGINE_API_KEY) {
    headers["X-API-Key"] = ENGINE_API_KEY;
  }
}

/** Headers for browser → engine fetch (JSON + optional auth). */
export function engineHeaders(extra?: HeadersInit): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  applyAuthHeaders(headers);
  if (!extra) return headers;
  if (extra instanceof Headers) {
    extra.forEach((value, key) => {
      headers[key] = value;
    });
    return headers;
  }
  if (Array.isArray(extra)) {
    for (const [key, value] of extra) headers[key] = value;
    return headers;
  }
  return { ...headers, ...extra };
}

/** Auth headers without Content-Type (for multipart FormData). */
export function engineAuthHeaders(): HeadersInit {
  const headers: Record<string, string> = {};
  applyAuthHeaders(headers);
  return headers;
}

/**
 * Browser → engine fetch with credentials (session cookie) + auth headers.
 * Prefer this over raw fetch for all engine calls.
 * On 401 with a stored session, clears zombie local session so RootApp shows login.
 */
export async function engineFetch(input: string, init?: RequestInit): Promise<Response> {
  const headers = engineHeaders(init?.headers);
  let res: Response;
  // FormData must not force Content-Type (boundary)
  if (init?.body instanceof FormData) {
    const h = { ...(headers as Record<string, string>) };
    delete h["Content-Type"];
    res = await fetch(input, {
      ...init,
      credentials: "include",
      headers: h,
    });
  } else {
    res = await fetch(input, {
      ...init,
      credentials: "include",
      headers,
    });
  }
  if (res.status === 401) {
    handleUnauthorizedSession();
  }
  return res;
}
